import { GrundstueckId, GrundstueckListEntry } from '../../../core/gb-api';
import { Component, Input, OnDestroy } from '@angular/core';
import { AppConfigService } from '../../../core/services/app-config.service';
import { TimepointService } from '../../../core/services/timepoint.service';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { ListLoaderInfiniteBuilder } from '../../../shared/components/list-loader/list-loader-infinite';
import { LastRechtRootEntryVm, StandardrechtViewmodelService } from '../../../core/services/standardrecht-viewmodel.service';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { map, tap } from 'rxjs/operators';
import { StammgrundstueckComponent } from '../../../shared/widgets/grundstueck/table-stammgrundstueck/stammgrundstueck.component';
import { BeschreibungTableWrapperComponent } from '../../../shared/components/table/beschreibung-table-wrapper.component';
import { StandardrechtTableComponent } from '../../../shared/widgets/standardrecht-table/standardrecht-table.component';
import { SonderrechtComponent } from '../../../shared/widgets/grundstueck/table-sonderrecht/sonderrecht.component';
import { DominierteGrundstueckeComponent } from '../../../shared/widgets/grundstueck/table-dominierte-grundstuecke/dominierte-grundstuecke.component';
import { KuhrechtComponent } from '../../../shared/widgets/grundstueck/table-kuhrecht/kuhrecht.component';
import { AlpgrundstueckeComponent } from '../../../shared/widgets/grundstueck/table-alpgrundstuecke/alpgrundstuecke.component';
import { GrundstueckBemerkungComponent } from '../../../shared/widgets/grundstueck/grundstueck-bemerkung/grundstueck-bemerkung.component';
import { TranslatePipe } from '@ngx-translate/core';
import GrundstueckArtEnum = GrundstueckListEntry.GrundstueckArtEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-grundbuch',
  templateUrl: './grundbuch.component.html',
  styleUrls: ['./grundbuch.component.scss'],
  imports: [
    StammgrundstueckComponent,
    BeschreibungTableWrapperComponent,
    StandardrechtTableComponent,
    SonderrechtComponent,
    DominierteGrundstueckeComponent,
    KuhrechtComponent,
    AlpgrundstueckeComponent,
    GrundstueckBemerkungComponent,
    TranslatePipe
  ]
})
export class GrundbuchComponent extends AbstractComponent implements OnDestroy {
  @Input()
  art: GrundstueckArtEnum;
  @Input()
  tableFullWidth = false;
  @Input()
  hasRight = true;

  _grundstueckId: GrundstueckId;

  hasPermissionSeybuch: Boolean;
  hasPermissionDominierteGrundstuecke: Boolean;

  sdrDienstbarkeitLoader: ListLoader<LastRechtRootEntryVm>;
  dienstbarkeitenErsterfassungUebergeben: boolean;
  hasRightAnzeigeEigentum: boolean;
  hasRightDienstbarkeiten: Boolean;

  private readonly CHUNK_SIZE = 5;
  private readonly PAGE_SIZE = 5;
  private readonly CURRENT_PAGE = 1;

  grundstueckArtEnum = GrundstueckArtEnum;

  constructor(
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private permissionService: AppPermissionService,
    private appFormatConfigService: FormatConfigService,
    private standardrechtViewmodelService: StandardrechtViewmodelService
  ) {
    super();
    keysToTranslate<string>([
      'title.Grundbuch',
      'Stammgrundstueck',
      'Anteil',
      'Rechtsgrund',
      'SelbststaendigUndDauerndeRechte',
      'Sonderrecht',
      'DominierteGrundstuecke',
      'Alpgrundstuecke',
      'Kuhrechte'
    ]);
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.hasPermissionSeybuch = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.SEYBUCH_FOLIO);
      })
    );
  }

  @Input()
  set grundstueckId(value: GrundstueckId) {
    this._grundstueckId = value;
    if (!value) {
      return;
    }
    this.addSub(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        this.update();
      })
    );
  }

  get grundstueckId(): GrundstueckId {
    return this._grundstueckId;
  }

  update() {
    this.checkPermissionDominierteGrundstuecke();
    this.checkPermissionAnzeigeEigentum();
    this.checkPermissionDienstbarkeiten();
    const z = this.timepointService.timepointCtx;

    this.sdrDienstbarkeitLoader = this.createSdrDienstbarkeitLoader();
  }

  private createSdrDienstbarkeitLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadSichtByGrundstueckId(this.grundstueckId, 'SDR_DIENSTBARKEIT', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.dienstbarkeitenErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  checkPermissionDominierteGrundstuecke() {
    this.hasPermissionDominierteGrundstuecke = this.permissionService.hasPermission('ANZEIGE_DOMINIERTE_GRUNDSTUECKE');
  }

  checkPermissionAnzeigeEigentum() {
    this.hasRightAnzeigeEigentum = this.permissionService.hasPermission('ANZEIGE_EIGENTUM');
  }

  checkPermissionDienstbarkeiten() {
    this.hasRightDienstbarkeiten = this.permissionService.hasPermission('ANZEIGE_DIENSTBARKEITEN');
  }
}
